import React, { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, Underline, Heading3, List, ListOrdered, Link2, Undo2, Redo2 } from "lucide-react";
import { cx } from "../../pages/admin/_shared";

const ToolBtn = ({ active, disabled, onClick, label, icon: Icon }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    aria-pressed={active}
    className={cx(
      "inline-flex h-8 w-8 items-center justify-center rounded-md transition disabled:opacity-40",
      active ? "bg-admin-primary/10 text-admin-primary" : "text-admin-textMuted hover:bg-admin-muted hover:text-admin-text"
    )}
  >
    <Icon size={15} />
  </button>
);

/** Editor HTML gọn (tiptap) — toolbar tối giản: đậm, nghiêng, gạch chân, tiêu đề, danh sách, liên kết. */
export default function RichTextEditor({ value, onChange, placeholder = "", invalid = false, minHeight = 160 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } },
      }),
    ],
    content: value || "",
    // Cập nhật trạng thái nút toolbar khi di chuyển con trỏ
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        class: "loc-rte-content focus:outline-none",
        style: `min-height:${minHeight}px`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.isEmpty ? "" : ed.getHTML();
      onChange?.(html);
    },
  });

  // Đồng bộ khi giá trị đổi từ bên ngoài (mở địa điểm khác, khôi phục nháp)
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? "" : editor.getHTML();
    if ((value || "") !== current) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  const setLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Nhập liên kết (để trống để bỏ liên kết)", prev);
    if (url === null) return;
    if (!url.trim()) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  const isEmpty = !editor || editor.isEmpty;

  return (
    <div
      className={cx(
        "overflow-hidden rounded-lg border bg-admin-surface transition focus-within:ring-4",
        invalid
          ? "border-admin-danger focus-within:ring-admin-danger/10"
          : "border-admin-border hover:border-admin-textMuted/40 focus-within:border-admin-primary focus-within:ring-admin-primary/10"
      )}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-admin-border bg-admin-canvas px-1.5 py-1">
        <ToolBtn icon={Bold} label="Đậm" active={editor?.isActive("bold")} onClick={() => editor?.chain().focus().toggleBold().run()} />
        <ToolBtn icon={Italic} label="Nghiêng" active={editor?.isActive("italic")} onClick={() => editor?.chain().focus().toggleItalic().run()} />
        <ToolBtn icon={Underline} label="Gạch chân" active={editor?.isActive("underline")} onClick={() => editor?.chain().focus().toggleUnderline().run()} />
        <span className="mx-1 h-5 w-px bg-admin-border" aria-hidden />
        <ToolBtn icon={Heading3} label="Tiêu đề nhỏ" active={editor?.isActive("heading", { level: 3 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} />
        <ToolBtn icon={List} label="Danh sách" active={editor?.isActive("bulletList")} onClick={() => editor?.chain().focus().toggleBulletList().run()} />
        <ToolBtn icon={ListOrdered} label="Danh sách số" active={editor?.isActive("orderedList")} onClick={() => editor?.chain().focus().toggleOrderedList().run()} />
        <ToolBtn icon={Link2} label="Liên kết" active={editor?.isActive("link")} onClick={setLink} />
        <span className="ml-auto flex items-center gap-0.5">
          <ToolBtn icon={Undo2} label="Hoàn tác" disabled={!editor?.can().undo()} onClick={() => editor?.chain().focus().undo().run()} />
          <ToolBtn icon={Redo2} label="Làm lại" disabled={!editor?.can().redo()} onClick={() => editor?.chain().focus().redo().run()} />
        </span>
      </div>
      <div className="loc-rte relative px-3 py-2.5 text-sm text-admin-text">
        {isEmpty && placeholder ? (
          <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-admin-textMuted/70">{placeholder}</span>
        ) : null}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
